// Pure helpers and Chart.js option builders for the Talk BI charts (spec §7). No DOM: the
// components in bi-charts.jsx resolve the theme and measure text; this file shapes data and options.
import { money, qty, pct, share, monthShort, monthLong, entityColor } from './bi-format.mjs';

const FONT = "'Heebo', Arial, sans-serif";
const KINDS = { money, qty, pct, share };
const DIM = '47'; // 28% alpha: an unselected slice fades but keeps its hue

export const formatter = (kind, compact = true) => (v) => (KINDS[kind] || qty)(v, { compact });
// Canvas text inherits the page's direction; an LTR isolate keeps "-₪5M" from reading "₪5M-" in RTL.
const ltr = (s) => `\u2066${s}\u2069`;

// Runner cells carry Decimal sums as strings.
export function toNumber(v) {
  const n = typeof v === 'string' && v.trim() ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

// A Hebrew-first label keeps the ellipsis at its own end even on an LTR canvas.
export const clip = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…${/^[^\p{L}]*[\u0590-\u05FF]/u.test(s) ? '\u200f' : ''}` : s);
export const shareOf = (v, total) => (total > 0 && Number.isFinite(v) ? Math.round((v / total) * 1000) / 10 : null);

export function average(values) {
  const xs = values.filter(Number.isFinite);
  return xs.length > 1 ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
}

export function summary(pairs, kind, lang) {
  const f = formatter(kind);
  const head = pairs.slice(0, 8).map(([label, v]) => `${label} ${f(v)}`).join(', ');
  return pairs.length > 8 ? `${head}${lang === 'he' ? ' ועוד' : ', …'}` : head;
}

// Chart.js bar elements: x is the bar's end pixel, base its baseline. The label sits past the end.
export const labelAnchor = ({ x, base }, gap = 6) =>
  x < base ? { textAlign: 'right', x: x - gap } : { textAlign: 'left', x: x + gap };

// Positive bars grow toward the outer edge (left in RTL), so their labels need that much padding.
export function rankedRoom(values, rtl, labelWidth) {
  const outer = values.some((v) => Number.isFinite(v) && v >= 0) ? labelWidth : 0;
  return rtl ? { left: outer, right: 0 } : { left: 0, right: outer };
}

// Negative bars grow back toward the category labels, so the axis runs past the lowest one by
// its label's width: solve (low - min) / (high - min) * width = label for min.
export function negativeMin(low, high, label, width) {
  const r = Math.min(0.6, label / Math.max(width, 1));
  return (low - r * high) / (1 - r);
}

// The doctor's {count, sum?} for the categories past the top N; never drawn as a bar (spec 7.2.4).
export function restNote(rest, shown, kind, lang) {
  if (!rest || !(rest.count > 0)) return null;
  const he = lang === 'he';
  const n = qty(rest.count);
  if (!Number.isFinite(rest.sum)) return he ? `ועוד ${n} שלא מוצגים בגרף.` : `${n} more are not drawn.`;
  const vals = shown.filter(Number.isFinite);
  const total = vals.reduce((a, b) => a + b, 0) + rest.sum;
  const part = vals.every((v) => v >= 0) && rest.sum >= 0 ? shareOf(rest.sum, total) : null;
  const sum = formatter(kind)(rest.sum);
  let text = he ? `שאר ה־${n}: ${sum}` : `The other ${n}: ${sum}`;
  if (part !== null) text += he ? `, ${share(part)} מהסך` : `, ${share(part)} of the total`;
  text += '.';
  if (rest.sum > Math.max(0, ...vals)) text += he ? ' לא מוצגים בגרף כדי לא לגמד את המובילים.' : ' Not drawn, so the leaders stay readable.';
  return text;
}

const withAlpha = (c, aa) => (/^#[0-9a-f]{6}$/i.test(c) ? `${c}${aa}` : c);

// Colour follows the entity's slot in a stable order; past the five hues, and the rest bucket, are grey.
export function sliceColors(slices, { order, selected = null, read } = {}) {
  const slots = order.slice(0, 5);
  return slices.map((s) => {
    const c = entityColor(s.other ? undefined : s.key, s.other ? [] : slots, read);
    return selected != null && s.key !== selected ? withAlpha(c, DIM) : c;
  });
}

const enMonth = (m, month) => new Intl.DateTimeFormat('en-US', { month, timeZone: 'UTC' }).format(new Date(Date.UTC(2024, m - 1, 15)));

function monthOf(x) {
  if (Number.isInteger(x) || /^\d{1,2}$/.test(String(x))) {
    const m = Number(x);
    return m >= 1 && m <= 12 ? { m } : null;
  }
  const hit = /^(\d{4})-(\d{2})/.exec(String(x));
  return hit && +hit[2] >= 1 && +hit[2] <= 12 ? { y: +hit[1], m: +hit[2] } : null;
}

// Month names for a time axis (Hebrew by default); the year joins a tick only when the span crosses one.
export function timeAxis(xs, lang = 'he') {
  const parts = xs.map(monthOf);
  // two points in one month are days or weeks, not months: keep their own dates
  if (!parts.length || !parts.every(Boolean) || new Set(parts.map((p) => `${p.y}-${p.m}`)).size < parts.length) return { ticks: xs.map(String), titles: xs.map(String) };
  const short = lang === 'he' ? monthShort : (m) => enMonth(m, 'short');
  const long = lang === 'he' ? monthLong : (m) => enMonth(m, 'long');
  const years = new Set(parts.map((p) => p.y)).size > 1;
  return {
    ticks: parts.map((p) => (years && p.y ? `${short(p.m)} ${String(p.y).slice(2)}` : short(p.m))),
    titles: parts.map((p) => (p.y ? `${long(p.m)} ${p.y}` : long(p.m))),
  };
}

const FALLBACK = {
  card: '#fff', ink: '#24332c', ink2: '#3d4e43', ink3: '#607065', line: '#dde3da', lineSoft: '#eef1eb',
  compare: '#819182', other: '#c1ccc2', c: ['#2f8f63', '#eda100', '#e87ba4', '#6250d6', '#eb6834'],
};

// Canvas cannot resolve var(); the components read the page's tokens once per build.
export function theme(read) {
  const v = (name, fb) => (read(name) || '').trim() || fb;
  return {
    card: v('--card', FALLBACK.card), ink: v('--ink', FALLBACK.ink), ink2: v('--ink-2', FALLBACK.ink2),
    ink3: v('--ink-3', FALLBACK.ink3), line: v('--line', FALLBACK.line), lineSoft: v('--line-soft', FALLBACK.lineSoft),
    compare: v('--bi-compare', FALLBACK.compare), other: v('--bi-other', FALLBACK.other),
    c: FALLBACK.c.map((fb, i) => v(`--bi-c${i + 1}`, fb)),
  };
}

const tip = (T, rtl, extra) => ({
  rtl, textDirection: rtl ? 'rtl' : 'ltr', backgroundColor: T.card, titleColor: T.ink, bodyColor: T.ink2,
  footerColor: T.ink3, borderColor: T.line, borderWidth: 1, padding: 10, cornerRadius: 6, caretSize: 5,
  titleFont: { weight: '600', size: 13 }, bodyFont: { size: 12 }, footerFont: { size: 12, weight: '500' },
  boxPadding: 4, usePointStyle: true, ...extra,
});

const base = (rtl) => ({ animation: false, responsive: true, maintainAspectRatio: false, locale: rtl ? 'he-IL' : 'en-US' });
const axisTicks = (T) => ({ color: T.ink3, font: { size: 11.5 } });
const legend = (T, rtl, pointStyle) => ({
  position: 'top', align: 'start', rtl, textDirection: rtl ? 'rtl' : 'ltr',
  labels: { color: T.ink2, usePointStyle: true, pointStyle, boxWidth: pointStyle === 'line' ? 24 : 10, font: { size: 12 } },
});

// Chart.js passes plugin options through unresolved, so a formatter can travel in them.
const valueLabels = {
  id: 'biValueLabels',
  afterDatasetsDraw(chart, _args, opts) {
    const { ctx } = chart;
    const data = chart.data.datasets[0].data;
    ctx.save();
    ctx.font = `500 12px ${FONT}`;
    ctx.fillStyle = opts.color;
    ctx.textBaseline = 'middle';
    ctx.direction = 'ltr';
    chart.getDatasetMeta(0).data.forEach((bar, i) => {
      if (!Number.isFinite(data[i])) return;
      const at = labelAnchor(bar);
      ctx.textAlign = at.textAlign;
      ctx.fillText(opts.format(data[i]), at.x, bar.y);
    });
    ctx.restore();
  },
};

const crosshair = {
  id: 'biCrosshair',
  afterDatasetsDraw(chart, _args, opts) {
    const active = chart.tooltip?.getActiveElements();
    if (!active?.length) return;
    const { ctx, chartArea: { top, bottom } } = chart;
    const x = active[0].element.x;
    ctx.save();
    ctx.strokeStyle = opts.color;
    ctx.globalAlpha = 0.45;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
    ctx.restore();
  },
};

const averageLine = {
  id: 'biAverage',
  afterDatasetsDraw(chart, _args, opts) {
    if (opts.value == null) return;
    const { ctx, chartArea: { left, right } } = chart;
    const y = chart.scales.y.getPixelForValue(opts.value);
    ctx.save();
    ctx.strokeStyle = opts.color;
    ctx.globalAlpha = 0.6;
    ctx.setLineDash([2, 3]);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(right, y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
    ctx.font = `500 11px ${FONT}`;
    ctx.direction = opts.rtl ? 'rtl' : 'ltr';
    const w = ctx.measureText(opts.text).width + 10;
    // Away from the value axis: the left end in RTL, the right end in LTR.
    const x0 = opts.rtl ? left + 2 : right - 2 - w;
    ctx.fillStyle = opts.card;
    ctx.fillRect(x0, y - 9, w, 18);
    ctx.fillStyle = opts.color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(opts.text, x0 + w / 2, y);
    ctx.restore();
  },
};

export function rankedConfig({ labels, values, kind, rtl, theme: T, labelWidth }) {
  const full = formatter(kind, false);
  const low = Math.min(0, ...values.filter(Number.isFinite));
  return {
    type: 'bar',
    data: {
      labels,
      datasets: [{
        data: values, backgroundColor: T.c[0], hoverBackgroundColor: T.c[0], borderRadius: 4,
        borderSkipped: 'start', barPercentage: 0.74, categoryPercentage: 0.9,
      }],
    },
    plugins: [valueLabels],
    options: {
      ...base(rtl),
      indexAxis: 'y',
      layout: { padding: rankedRoom(values, rtl, labelWidth) },
      scales: {
        x: {
          reverse: rtl, beginAtZero: true, grace: '4%', border: { display: false }, ticks: { display: false },
          grid: { color: (c) => (c.tick?.value === 0 ? T.line : T.lineSoft) },
          afterDataLimits: low < 0 ? (scale) => { scale.min = Math.min(scale.min, negativeMin(low, scale.max, labelWidth, scale.width)); } : undefined,
        },
        y: {
          position: rtl ? 'right' : 'left', grid: { display: false }, border: { color: T.line },
          ticks: { color: T.ink2, font: { size: 12 }, callback(_v, i) { return clip(labels[i] ?? '', this.chart.width < 420 ? 18 : 30); } },
        },
      },
      plugins: {
        legend: { display: false },
        biValueLabels: { format: formatter(kind), color: T.ink2 },
        tooltip: tip(T, rtl, { displayColors: false, callbacks: { title: (items) => labels[items[0].dataIndex], label: (c) => ` ${ltr(full(c.raw))}` } }),
      },
    },
  };
}

export function trendConfig({ ticks, titles, values, compare = null, names = [], kind, rtl, theme: T, reference }) {
  const full = formatter(kind, false);
  const short = formatter(kind);
  const datasets = [{
    label: names[0] ?? (rtl ? 'התקופה' : 'This period'), data: values, borderColor: T.c[0], backgroundColor: T.c[0],
    borderWidth: 2, pointRadius: 3, pointHoverRadius: 5, pointBackgroundColor: T.card, pointBorderWidth: 2, tension: 0.2, clip: 6,
  }];
  if (compare) {
    datasets.push({
      label: names[1] ?? (rtl ? 'התקופה הקודמת' : 'Previous period'), data: compare, borderColor: T.compare,
      backgroundColor: T.compare, borderWidth: 2, borderDash: [6, 4], pointRadius: 0, pointHoverRadius: 4, tension: 0.2, clip: 6,
    });
  }
  const avg = reference === 'average' ? average(values) : null;
  const change = (items) => {
    const [a, b] = items.map((it) => it.raw);
    if (items.length < 2 || !Number.isFinite(a) || !b) return '';
    return `${rtl ? 'שינוי' : 'Change'}: ${ltr(pct(Math.round(((a - b) / Math.abs(b)) * 1000) / 10))}`;
  };
  return {
    type: 'line',
    data: { labels: ticks, datasets },
    plugins: [crosshair, averageLine],
    options: {
      ...base(rtl),
      layout: { padding: { left: 6, right: 6 } },
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: { grid: { display: false }, border: { color: T.line }, ticks: { ...axisTicks(T), maxRotation: 0, autoSkipPadding: 10 } },
        y: {
          beginAtZero: true, position: rtl ? 'right' : 'left', grid: { color: T.lineSoft }, border: { display: false },
          ticks: { ...axisTicks(T), maxTicksLimit: 6, callback: (v) => ltr(short(v)) },
        },
      },
      plugins: {
        biCrosshair: { color: T.ink3 },
        biAverage: { value: avg, text: avg == null ? '' : `${rtl ? 'ממוצע' : 'Average'} ${ltr(short(avg))}`, color: T.ink3, card: T.card, rtl },
        legend: { display: datasets.length > 1, ...legend(T, rtl, 'line') },
        tooltip: tip(T, rtl, {
          callbacks: { title: (items) => titles[items[0].dataIndex], label: (c) => ` ${c.dataset.label}: ${ltr(full(c.raw))}`, footer: change },
        }),
      },
    },
  };
}

export function donutConfig({ labels, values, colors, kind, rtl, theme: T, onPick }) {
  const full = formatter(kind, false);
  const total = values.reduce((a, v) => a + (v || 0), 0);
  return {
    type: 'doughnut',
    data: { labels, datasets: [{ data: values, backgroundColor: colors, hoverBackgroundColor: colors, borderColor: T.card, borderWidth: 2, hoverOffset: 4 }] },
    options: {
      ...base(rtl),
      cutout: '64%',
      onClick: (_e, els) => { if (els.length && onPick) onPick(els[0].index); },
      plugins: {
        legend: { display: false },
        tooltip: tip(T, rtl, { callbacks: { label: (c) => ` ${ltr(full(c.raw))} · ${ltr(share(shareOf(c.raw, total)))}` } }),
      },
    },
  };
}

export function stackedConfig({ labels, titles = labels, series, kind, rtl, theme: T }) {
  const full = formatter(kind, false);
  const short = formatter(kind);
  return {
    type: 'bar',
    data: {
      labels,
      datasets: series.map((s) => ({
        label: s.name, data: s.values, backgroundColor: s.color, hoverBackgroundColor: s.color,
        borderColor: T.card, borderWidth: 1, barPercentage: 0.72, categoryPercentage: 0.86,
      })),
    },
    options: {
      ...base(rtl),
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: { stacked: true, grid: { display: false }, border: { color: T.line }, ticks: { ...axisTicks(T), maxRotation: 0, autoSkipPadding: 10 } },
        y: {
          stacked: true, beginAtZero: true, position: rtl ? 'right' : 'left', grid: { color: T.lineSoft }, border: { display: false },
          ticks: { ...axisTicks(T), maxTicksLimit: 6, callback: (v) => ltr(short(v)) },
        },
      },
      plugins: {
        legend: { display: true, ...legend(T, rtl, 'rectRounded') },
        tooltip: tip(T, rtl, {
          callbacks: {
            title: (items) => titles[items[0].dataIndex],
            label: (c) => ` ${c.dataset.label}: ${ltr(full(c.raw))}`,
            footer: (items) => `${rtl ? 'סה״כ' : 'Total'}: ${ltr(full(items.reduce((a, it) => a + (Number.isFinite(it.raw) ? it.raw : 0), 0)))}`,
          },
        }),
      },
    },
  };
}
